<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import Sidebar from './Sidebar.vue'
import SuperAdminToolbar from '@/components/SuperAdminToolbar.vue'

const route = useRoute()

const authStore = useAuthStore()
const uiStore = useUiStore()

const pageTitle = computed(() => (route.meta?.title as string) || 'Dashboard')

const contentClass = computed(() => ({
  'main-content': true,
  'main-content--sidebar-collapsed': uiStore.sidebarCollapsed,
}))

/**
 * Force child views to re-mount when the active organization changes.
 * This ensures all org-scoped views fetch fresh data after an org switch,
 * even if the route path itself hasn't changed.
 */
const orgRouteKey = computed(() => authStore.currentOrganizationId || 'default')

onMounted(async () => {
  uiStore.initializeTheme()
  await authStore.initialize()
})

const handleLogout = () => {
  authStore.logout()
}
</script>

<template>
  <div class="app-layout">
    <!-- Skip Navigation (ADA/WCAG) -->
    <a href="#main-content" class="skip-nav">Skip to main content</a>
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content Area -->
    <div :class="contentClass">
      <!-- Header -->
      <header class="main-header">
        <div class="main-header__left">
          <h1 class="main-header__title">{{ pageTitle }}</h1>
        </div>
        
        <div class="main-header__right">
          <!-- Theme Toggle -->
          <button
            class="header-button"
            :title="uiStore.isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="uiStore.toggleTheme"
          >
            <!-- Sun icon for dark mode (shows when dark, click to go light) -->
            <svg v-if="uiStore.isDarkMode" class="header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <!-- Moon icon for light mode (shows when light, click to go dark) -->
            <svg v-else class="header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </button>
          
          <!-- Logout -->
          <button
            class="header-button"
            title="Logout"
            @click="handleLogout"
          >
            <svg class="header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      <!-- Page Content -->
      <main id="main-content" class="page-content">
        <router-view :key="orgRouteKey" />
      </main>
    </div>

    <!-- Super Admin Toolbar -->
    <SuperAdminToolbar v-if="authStore.isSuperAdmin" />

    <!-- Toast Container -->
    <Teleport to="body">
      <div class="toast-container" role="status" aria-live="polite">
        <TransitionGroup name="toast">
          <div
            v-for="toast in uiStore.toasts"
            :key="toast.id"
            :class="['toast', `toast--${toast.type}`]"
          >
            <span>{{ toast.message }}</span>
            <button
              class="toast__close"
              @click="uiStore.removeToast(toast.id)"
            >
              &times;
            </button>
          </div>
        </TransitionGroup>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Skip Navigation (visible only on focus for keyboard users) */
.skip-nav {
  position: absolute;
  top: -100%;
  left: 1rem;
  padding: 0.5rem 1rem;
  background: var(--bg-elevated);
  color: var(--text-primary);
  border: 2px solid var(--color-primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 600;
  text-decoration: none;
  z-index: 10000;
  transition: top 0.2s;
}

.skip-nav:focus {
  top: 0.75rem;
}

.app-layout {
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  margin-left: var(--sidebar-width);
  min-height: 100vh;
  transition: margin-left var(--transition-slow), background-color var(--transition-theme);
  background-color: var(--bg-primary);
  /* Blueprint/engineering paper grid - light mode */
  background-image: 
    /* Major grid lines (80px) - slightly more visible */
    linear-gradient(rgba(100, 150, 180, 0.12) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 150, 180, 0.12) 1px, transparent 1px),
    /* Minor grid lines (20px) - subtle */
    linear-gradient(rgba(100, 150, 180, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 150, 180, 0.05) 1px, transparent 1px);
  background-size: 80px 80px, 80px 80px, 20px 20px, 20px 20px;
  background-attachment: fixed;
}

:root.dark-mode .main-content {
  /* Blueprint grid - dark mode (blue tint, subtle) */
  background-image: 
    /* Major grid lines */
    linear-gradient(rgba(100, 160, 200, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 160, 200, 0.08) 1px, transparent 1px),
    /* Minor grid lines */
    linear-gradient(rgba(100, 160, 200, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(100, 160, 200, 0.03) 1px, transparent 1px);
  background-size: 80px 80px, 80px 80px, 20px 20px, 20px 20px;
}

.main-content--sidebar-collapsed {
  margin-left: var(--sidebar-collapsed-width);
}

.main-header {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  height: var(--header-height);
  z-index: var(--z-sticky);
  backdrop-filter: blur(10px);
  transition: background-color var(--transition-theme), border-color var(--transition-theme);
}

.main-header__title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: var(--font-display);
}

.main-header__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-button {
  padding: 0.5rem;
  border-radius: 8px;
  color: var(--text-muted);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.header-button:hover {
  color: var(--text-primary);
  background: var(--bg-hover);
}

.header-icon {
  width: 20px;
  height: 20px;
}

.page-content {
  padding: 2rem;
}

/* Toast styles */
.toast-container {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: var(--z-toast);
}

.toast {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: var(--radius-sm);
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--border-color);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  min-width: 280px;
  max-width: 420px;
  font-size: var(--text-sm);
  color: var(--text-primary);
  line-height: 1.4;
}

.toast--success {
  border-left-color: var(--color-success);
}

.toast--error {
  border-left-color: var(--color-error);
}

.toast--warning {
  border-left-color: var(--color-warning);
}

.toast--info {
  border-left-color: var(--color-primary);
}

.toast__close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 1rem;
  line-height: 1;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.toast__close:hover {
  opacity: 1;
}

/* Toast transitions */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
